const nombre = (valeur) => valeur == null || (typeof valeur === 'string' && valeur.trim() === '') ? null : (['number','string'].includes(typeof valeur) ? Number(valeur) : NaN);
const arrondi = (valeur) => Math.round((Number(valeur) + Number.EPSILON) * 100) / 100;

export function normaliserMajorationAdherent(valeur) {
  const majoration = nombre(valeur);
  if (majoration == null) return null;
  if (!Number.isFinite(majoration) || majoration < 0 || majoration > 100) throw new Error('La majoration prix adhérent doit être comprise entre 0 et 100 %.');
  return majoration;
}

export function coefficientMajorationAdherent(valeur) {
  const majoration = normaliserMajorationAdherent(valeur);
  return majoration == null ? null : arrondi(1 + majoration / 100);
}

export function prixAdherentReference(produit = {}) {
  const interclub = String(produit.source_origine || '').toUpperCase() === 'INTERCLUB';
  const source = donneesTarifairesSource(produit);
  // Pour un article CRM administré, le net du devis est la référence canonique.
  // Les champs de projection publique restent utilisés lorsqu'aucune donnée source
  // privée n'est présente (portail adhérent en lecture seule).
  if (!interclub && Object.prototype.hasOwnProperty.call(produit, 'prix_reference_ttc') && source.net != null && (
    Object.prototype.hasOwnProperty.call(produit, 'prix_source_ttc')
    || Object.prototype.hasOwnProperty.call(produit.source_snapshot || {}, 'prix_source_ttc')
    || Object.prototype.hasOwnProperty.call(produit.source_snapshot || {}, 'prix_net_unitaire_ttc')
  )) return source.net;
  return nombre(produit.prix_reference_ttc)
    ?? nombre(produit.prix_adherent_retenu_ttc)
    ?? (interclub ? null : (nombre(produit.prix_adherent_ttc) ?? source.net));
}

export function prixPromoAdherentReference(produit = {}) {
  const interclub = String(produit.source_origine || '').toUpperCase() === 'INTERCLUB';
  return nombre(produit.prix_promo_retenu_ttc)
    ?? (interclub ? null : nombre(produit.prix_promo_ttc));
}

export function calculerPrixAdherentPropose(prixClubNetTtc, majorationPourcent) {
  const net = nombre(prixClubNetTtc);
  const majoration = normaliserMajorationAdherent(majorationPourcent);
  return net == null || !Number.isFinite(net) || majoration == null ? null : arrondi(net * (1 + majoration / 100));
}

export function donneesTarifairesSource(produit = {}) {
  const snapshot = produit.source_snapshot || {};
  const interclub = String(produit.source_origine || '').toUpperCase() === 'INTERCLUB';
  const brut = nombre(produit.prix_source_avant_remise_ttc) ?? nombre(snapshot.prix_source_avant_remise_ttc) ?? nombre(snapshot.prix_unitaire_ttc) ?? nombre(snapshot.prix_vente_ttc) ?? nombre(produit.prix_club_ttc) ?? nombre(produit.prix_adherent_ttc);
  const remise = nombre(produit.remise_source_pourcent) ?? nombre(snapshot.remise_pourcent) ?? nombre(snapshot.remise);
  const net = nombre(produit.prix_source_ttc) ?? nombre(snapshot.prix_source_ttc) ?? nombre(snapshot.prix_net_unitaire_ttc) ?? (interclub ? nombre(produit.prix_club_ttc) : null) ?? (brut == null ? null : arrondi(brut * (1 - Number(remise || 0) / 100)));
  return { brut, remise, net };
}
export function remiseBoutiqueActive(produit = {}, campagne = {}) {
  const mode = String(produit.remise_mode || 'AUCUNE').toUpperCase();
  if (mode === 'GENERAL') return { mode, pourcent: nombre(campagne.remise_generale_pourcent) ?? 0 };
  if (mode === 'SPECIFIQUE') return { mode, pourcent: nombre(produit.remise_specifique_pourcent) ?? 0 };
  return { mode: 'AUCUNE', pourcent: 0 };
}
export function prixFinalBoutique(produit = {}, campagne = {}) {
  const reference = prixAdherentReference(produit);
  if (reference == null || !Number.isFinite(reference) || reference <= 0) return null;
  const { mode, pourcent } = remiseBoutiqueActive(produit, campagne);
  if (!Number.isFinite(pourcent) || pourcent < 0 || pourcent > 100) return null;
  if (mode !== 'AUCUNE') return arrondi(reference * (1 - pourcent / 100));
  const promo = prixPromoAdherentReference(produit);
  return promo != null && Number.isFinite(promo) && promo > 0 && promo < reference ? arrondi(promo) : arrondi(reference);
}

// Une date normalisée par Date (ex. 30 février) n'est pas une preuve valide.
function datePreuvePrix(valeur, finJournee = false) {
  if (typeof valeur !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(valeur)) return null;
  const date = new Date(`${valeur}T00:00:00`);
  const [annee, mois, jour] = valeur.split('-').map(Number);
  if (!Number.isFinite(date.getTime()) || date.getFullYear() !== annee || date.getMonth() + 1 !== mois || date.getDate() !== jour) return null;
  if (finJournee) date.setHours(23, 59, 59, 999);
  return date;
}
export function preuvePrixBarre(campagne = {}, maintenant = new Date()) {
  const preuve = campagne?.communication_config?.prix_barres || {};
  const debut = datePreuvePrix(preuve.periode_debut);
  const fin = datePreuvePrix(preuve.periode_fin, true);
  const instant = maintenant instanceof Date ? maintenant : new Date(maintenant);
  // Compter les dates calendaires, même pendant un changement d'heure.
  const jourCalendaire = (date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;
  const conforme = preuve.actif !== false
    && preuve.prix_plus_bas_30_jours_confirme === true
    && typeof preuve.origine === 'string' && preuve.origine.trim().length >= 3
    && debut !== null && fin !== null && Number.isFinite(instant.getTime())
    && jourCalendaire(fin) - jourCalendaire(debut) >= 29 && fin.getTime() <= instant.getTime();
  return { conforme, preuve };
}

export function afficherPrixBarre(produit = {}, campagne = {}, maintenant = new Date(), contratArticle) {
  // Le 4e argument est un contrat explicite en mémoire, jamais un champ supposé persisté.
  // Même null/undefined explicite ne doit pas réactiver le chemin campagne historique.
  if (arguments.length >= 4) return decisionPrixBarreArticle(produit, campagne, contratArticle, maintenant).afficher;
  const montant = (valeur) => (typeof valeur === 'number' || (typeof valeur === 'string' && valeur.trim() !== '')) && Number.isFinite(Number(valeur));
  const reference = Number(produit.prix);
  const final = Number(produit.promo);
  return preuvePrixBarre(campagne, maintenant).conforme
    && montant(produit.prix) && reference > 0
    && montant(produit.promo) && final > 0 && final < reference;
}
// Décision locale du futur contrat article. Le raccordement stockage/projection est hors lot.
export function decisionPrixBarreArticle(produit = {}, campagne = {}, contratArticle = null, maintenant = new Date()) {
  const masquer = (raison, etat = 'MASQUE') => ({ afficher:false, etat, raison });
  const objet = valeur => valeur !== null && typeof valeur === 'object' && !Array.isArray(valeur);
  const texte = valeur => typeof valeur === 'string' && valeur.trim() !== '' ? valeur.trim() : null;
  const centimes = valeur => {
    const montant = nombre(valeur);
    if (montant == null || !Number.isFinite(montant) || montant <= 0) return null;
    const entier = Math.round(montant * 100);
    return Number.isSafeInteger(entier) && Math.abs(montant * 100 - entier) < 1e-7 ? entier : null;
  };
  if (!objet(contratArticle)) return masquer('CONTRAT_ARTICLE_ABSENT', 'BLOQUE');
  const mode = contratArticle.mode;
  if (!['ACTIF','INACTIF','HERITER'].includes(mode)) return masquer('MODE_INVALIDE');
  if (mode === 'INACTIF') return masquer('REFUS_ARTICLE');
  if (mode === 'HERITER' && campagne?.communication_config?.prix_barres?.actif === false) return masquer('CAMPAGNE_INACTIVE');
  if (!objet(produit) || !texte(produit.id) || !texte(produit.reference)) return masquer('IDENTITE_ARTICLE_ABSENTE', 'BLOQUE');
  const preuve = contratArticle.preuve;
  if (!objet(preuve)) return masquer('PREUVE_ARTICLE_ABSENTE');
  if (texte(preuve.produit_id) !== texte(produit.id) || texte(preuve.reference_article) !== texte(produit.reference)) return masquer('PREUVE_AUTRE_ARTICLE');
  // Un devis ou un prix source seul n'est jamais une preuve de prix pratiqué.
  if (!['HISTORIQUE_VENTES','REGISTRE_PRIX_PRATIQUES'].includes(preuve.origine) || !texte(preuve.justificatif_reference)) return masquer('ORIGINE_NON_JUSTIFIEE');
  const controle = preuvePrixBarre({ communication_config:{ prix_barres:{ ...preuve, actif:true } } }, maintenant);
  if (!controle.conforme) return masquer('PERIODE_OU_CONFIRMATION_INVALIDE');
  const ancien = centimes(preuve.prix_anterieur_ttc);
  const reference = centimes(produit.prix);
  const final = centimes(produit.promo);
  if (ancien == null || reference == null || final == null) return masquer('MONTANT_INVALIDE');
  if (ancien !== reference) return masquer('PRIX_ANTERIEUR_INCOHERENT');
  if (final >= ancien) return masquer('PRIX_FINAL_NON_INFERIEUR');
  return {
    afficher:true, etat:'AFFICHABLE_LOCALEMENT', raison:'PREUVE_ARTICLE_CONFORME',
    origine_activation:mode === 'HERITER' ? 'CAMPAGNE' : 'ARTICLE',
    prix_anterieur_ttc:ancien / 100, prix_final_ttc:final / 100, economie_ttc:(ancien - final) / 100
  };
}

export function contratPrixBarreArticle(campagne = {}, produitOuId = {}) {
  const id = typeof produitOuId === 'object' ? produitOuId?.id : produitOuId;
  const contrats = campagne?.communication_config?.prix_barres_articles;
  if (!id || contrats === null || typeof contrats !== 'object' || Array.isArray(contrats)) return null;
  const contrat = contrats[String(id)];
  return contrat !== null && typeof contrat === 'object' && !Array.isArray(contrat) ? contrat : null;
}

export function normaliserContratPrixBarreArticle(produit = {}, contrat = {}) {
  const mode = String(contrat?.mode || 'INACTIF').toUpperCase();
  if (!['ACTIF','INACTIF','HERITER'].includes(mode)) throw new Error('Mode de prix barré invalide.');
  if (mode === 'INACTIF') return { mode:'INACTIF', preuve:contrat?.preuve ?? null };
  const preuve = contrat?.preuve;
  // Le choix « prix barré » peut être enregistré avant que sa preuve soit prête.
  // La décision publique reste alors masquée et affiche automatiquement le prix net.
  if (preuve == null) return { mode, preuve:null };
  if (preuve === null || typeof preuve !== 'object' || Array.isArray(preuve)) throw new Error('Le justificatif du prix antérieur est obligatoire.');
  const prix = nombre(preuve.prix_anterieur_ttc);
  const origine = String(preuve.origine || '').trim().toUpperCase();
  const reference = String(preuve.justificatif_reference || '').trim();
  if (!String(produit.id || '').trim() || !String(produit.reference || '').trim()) throw new Error('Identité article incomplète.');
  if (!Number.isFinite(prix) || prix <= 0) throw new Error('Le prix antérieur doit être strictement positif.');
  if (!['HISTORIQUE_VENTES','REGISTRE_PRIX_PRATIQUES'].includes(origine)) throw new Error('Origine du justificatif invalide.');
  if (!reference) throw new Error('La référence du justificatif est obligatoire.');
  return {
    mode,
    preuve:{
      produit_id:String(produit.id), reference_article:String(produit.reference).trim(),
      prix_anterieur_ttc:arrondi(prix), origine, justificatif_reference:reference,
      periode_debut:String(preuve.periode_debut || ''), periode_fin:String(preuve.periode_fin || ''),
      prix_plus_bas_30_jours_confirme:preuve.prix_plus_bas_30_jours_confirme === true
    }
  };
}

export function definirContratPrixBarreArticle(communicationConfig = {}, produit = {}, contrat = {}) {
  const normalise = normaliserContratPrixBarreArticle(produit, contrat);
  const existants = communicationConfig?.prix_barres_articles;
  return {
    ...(communicationConfig || {}),
    prix_barres_articles:{
      ...(existants !== null && typeof existants === 'object' && !Array.isArray(existants) ? existants : {}),
      [String(produit.id)]:normalise
    }
  };
}

export function validerTvaArticle(valeur) {
  const tva = nombre(valeur);
  if (tva == null) return null;
  if (!Number.isFinite(tva) || tva < 0 || tva > 100) throw new Error('La TVA doit être comprise entre 0 et 100 %.');
  return tva;
}
export function margeInterne(produit = {}, campagne = {}) {
  if (String(produit.source_origine || '').toUpperCase() === 'INTERCLUB' && produit.prix_achat_source_disponible !== true) return null;
  const tva = nombre(produit.tva_pourcent);
  const paHt = nombre(produit.prix_achat_ht);
  const pvTtc = prixFinalBoutique(produit, campagne);
  if (tva == null || !Number.isFinite(tva) || tva < 0 || tva > 100 || paHt == null || !Number.isFinite(paHt) || pvTtc == null || !Number.isFinite(pvTtc)) return null;
  const pvHt = pvTtc / (1 + tva / 100);
  return pvHt <= 0 ? null : arrondi(((pvHt - paHt) / pvHt) * 100);
}

export function validerTarificationAdherent(produit = {}) {
  const retenu = nombre(produit.prix_adherent_retenu_ttc);
  const promo = nombre(produit.prix_promo_retenu_ttc);
  if (retenu != null && (!Number.isFinite(retenu) || retenu <= 0)) throw new Error('Le prix adhérent retenu doit être strictement positif.');
  if (promo != null && (!Number.isFinite(promo) || promo <= 0 || retenu == null || promo >= retenu)) throw new Error('Le prix promo adhérent doit être positif et strictement inférieur au prix adhérent retenu.');
  return { retenu, promo };
}
export function validerRemiseArticle(produit = {}) {
  const mode = String(produit.remise_mode || 'AUCUNE').toUpperCase();
  if (!['GENERAL','SPECIFIQUE','AUCUNE'].includes(mode)) throw new Error('Mode de remise article invalide.');
  if (mode === 'SPECIFIQUE') { const valeur=nombre(produit.remise_specifique_pourcent); if (valeur==null||!Number.isFinite(valeur)||valeur<0||valeur>100) throw new Error('La remise spécifique doit être comprise entre 0 et 100 %.'); return {mode,specifique:valeur}; }
  return {mode,specifique:null};
}
export function filtrerArticles(articles = [], {recherche='',famille='TOUTES',statut='TOUS'} = {}) {
  const terme=String(recherche).trim().toLocaleLowerCase('fr');
  return articles.filter((article)=>{const texte=`${article.reference||''} ${article.designation_boutique||article.designation||''}`.toLocaleLowerCase('fr');return(!terme||texte.includes(terme))&&(famille==='TOUTES'||String(article.famille_id||'')===String(famille))&&(statut==='TOUS'||(statut==='VISIBLE'?!!article.visible:!article.visible));});
}
